import { useCallback, useMemo } from 'react';
import { compact, get, isString, toPath } from 'lodash-es';
import { useFormContext, useWatch, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getModelAndAttributeFromPath } from '../utils/models';
import { useModel, useModelAndAttributeFromPath } from './models';
import { zodDefaultFromAttribute } from '../utils/zod';
import { RhinoAttribute, RhinoResource } from '../index';
// @ts-expect-error models/schemas.zod is generated in the client application
import { schemas } from 'models/schemas.zod';

type AttributeSchemaBuilder = (attribute: RhinoAttribute) => z.ZodType;

export const useSchema = (
  model: RhinoResource,
  paths?: PropertyKey[],
  options: { schemaFromAttribute?: AttributeSchemaBuilder } = {}
) => {
  const { schemaFromAttribute } = options;

  return useMemo(() => {
    const shape: Record<string, z.ZodType> = {};

    paths?.forEach((path) => {
      // Only string paths drive validation. JSX-element paths are render-only:
      // they may be conditionally rendered or carry server-normalized values
      // the generated schema can't reason about, so they are deliberately
      // excluded here and validated server-side. Fields that need client
      // validation are passed as string paths.
      if (!isString(path)) return;

      const [attributeModel, attribute, , plainPath] =
        getModelAndAttributeFromPath(model, path);

      // Filter mode builds per-attribute; validation mode reads the generated
      // model schema's field.
      // `ZodObject.shape[key]` is typed as core.$ZodType (the base), not the
      // classic ZodType — cast so the dynamically-assembled shape record stays
      // loosely typed, mirroring the old yup.AnySchema approach.
      let leaf: z.ZodType | undefined = schemaFromAttribute
        ? schemaFromAttribute(attribute!)
        : ((
            schemas[attributeModel!.model] as
              z.ZodObject<z.ZodRawShape> | undefined
          )?.shape?.[attribute!.name] as z.ZodType | undefined);

      if (!leaf) return;

      const pathParts = toPath(plainPath);
      pathParts
        .slice(1)
        .reverse()
        .forEach((part) => {
          leaf = z.object({ [part]: leaf as z.ZodType });
        });

      shape[pathParts[0]] = leaf as z.ZodType;
    });

    return z.object(shape).loose();
  }, [model, paths, schemaFromAttribute]);
};

export const useComputedPaths = (
  model: RhinoResource,
  paths: string[] | undefined,
  getDefaultAttributes: (model: RhinoResource) => RhinoAttribute[]
) => {
  const computedPaths = useMemo(() => {
    if (paths) return paths;

    return (
      getDefaultAttributes(model)
        // Do not include the owner
        .filter((a) => a.name !== model.ownedBy)
        .map((a) => a.name)
    );
  }, [model, paths, getDefaultAttributes]);

  return computedPaths;
};

export const useFieldInheritedProps = (
  props: {
    id?: string;
    className?: string;
    path?: string;
    [key: string]: unknown;
  },
  options: { prefix?: string | null; className?: string | null } = {
    prefix: null,
    className: null
  }
) => {
  // id and classes should not be inherited down a component tree
  const { id, className, ...inheritedProps } = props;

  const propReturns = useMemo(() => {
    const extractedProps = {
      id: id || [options.prefix, props.path].filter(Boolean).join('-'),
      className: [options.className, className].filter(Boolean).join(' ')
    };

    return {
      ...extractedProps,
      extractedProps,
      inheritedProps
    };
  }, [
    id,
    options.prefix,
    className,
    options.className,
    props.path,
    inheritedProps
  ]);

  return propReturns;
};

type FieldErrorPayload = { errors: Record<string, string[]> };

export const useFieldSetErrors = (
  setError: ReturnType<typeof useFormContext>['setError'] | null = null
) => {
  const formContext = useFormContext();

  return useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (error: FieldErrorPayload) => {
      const realSetError = setError || formContext.setError;

      Object.keys(error.errors).forEach((name) =>
        realSetError(name, {
          type: 'manual',
          message: error.errors[name][0]
        })
      );
    },
    [setError, formContext?.setError]
  );
};

export const useFieldRegister = (
  path: string,
  options: Parameters<ReturnType<typeof useFormContext>['register']>[1] = {}
) => {
  const { register } = useFormContext();

  return register(path, options);
};

export const useFieldError = (path: string) => {
  const {
    formState: { errors }
  } = useFormContext();

  // Cannot memoize this because the errors object is a stable object
  return get(errors, path);
};

export const useModelFieldGroup = ({
  model,
  ...props
}: {
  model: RhinoResource;
  path: string;
  label?: string;
  placeholder?: string;
  [key: string]: unknown;
}) => {
  const { path } = props;
  const { attribute: maybeAttribute } = useModelAndAttributeFromPath(
    model,
    path
  );
  const attribute = maybeAttribute!;

  const label = useMemo(
    () => props?.label || attribute.readableName,
    [attribute, props?.label]
  );

  const placeholder = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => props?.placeholder || label,
    [label, props?.placeholder]
  );

  return {
    attribute,
    model,
    fieldGroupProps: {
      label,
      nullable: attribute.nullable,
      placeholder,
      required: !!attribute.required,
      ...props
    }
  };
};

export const useModelDisplayGroup = useModelFieldGroup;

export const useModelFieldGroupEnum = ({
  children: propsChildren,
  ...props
}: Parameters<typeof useModelFieldGroup>[0] & {
  children?: React.ReactNode;
}) => {
  const inputProps = useModelFieldGroup(props);
  const { attribute } = inputProps;

  const children = useMemo(() => {
    // children can be a single element or an array
    if (propsChildren)
      return Array.isArray(propsChildren) ? propsChildren : [propsChildren];

    return (attribute.enum ?? []).map((e) => (
      <option key={String(e)} value={e as string}>
        {attribute.enumLabels?.[String(e)] || String(e)}
      </option>
    ));
  }, [attribute.enum, attribute.enumLabels, propsChildren]);

  const accessor = useCallback((value: unknown) => value || -1, []);
  const title = `${attribute.readableName}...`;

  return {
    ...inputProps,
    fieldGroupProps: {
      ...inputProps.fieldGroupProps,
      accessor,
      children,
      title
    }
  };
};

export const useModelFieldGroupIntegerSelect = (
  props: Parameters<typeof useModelFieldGroup>[0]
) => {
  const inputProps = useModelFieldGroup(props);
  const { attribute } = inputProps;
  // Translate to html input prop naming from the OpenAPI naming
  const { minimum: min = 0, maximum: max = 0 } = attribute;

  const children = useMemo(
    () =>
      Array.from({ length: max - min }, (_x, i) => ({
        id: i + min,
        display_name: `${i + min}`
      })),
    [min, max]
  );

  const accessor = useCallback((value: unknown) => value || -1, []);
  const title = `${attribute.readableName}...`;

  return {
    ...inputProps,
    fieldGroupProps: {
      ...inputProps.fieldGroupProps,
      accessor,
      children,
      title
    }
  };
};

export const useModelDisplayFieldGroupAttachment = (
  props: Parameters<typeof useModelFieldGroup>[0]
) => {
  const inputProps = useModelFieldGroup(props);

  const accessor = useCallback(
    (value: { url?: string } | undefined) => value?.url,
    []
  );
  const watch = useWatch({ name: props.path });

  const children = useMemo(
    () => props.children || watch?.display_name,
    [props.children, watch]
  );

  return {
    ...inputProps,
    fieldGroupProps: {
      ...inputProps.fieldGroupProps,
      accessor,
      children
    }
  };
};

export const useModelDisplayFieldGroupAttachmentImage = (
  props: Parameters<typeof useModelFieldGroup>[0]
) => {
  const inputProps = useModelFieldGroup(props);

  const accessor = useCallback(
    (value: { url?: string } | undefined) => value?.url,
    []
  );
  const watch = useWatch({ name: props.path });

  const alt = useMemo(() => watch?.display_name, [watch]);

  return {
    ...inputProps,
    fieldGroupProps: {
      ...inputProps.fieldGroupProps,
      accessor,
      alt
    }
  };
};

export const useDefaultValues = (
  model: RhinoResource,
  paths?: PropertyKey[],
  options: {
    schemaFromAttribute?: AttributeSchemaBuilder;
    extraDefaultValues?: object;
  } = {}
) => {
  const { schemaFromAttribute, extraDefaultValues } = options;

  const defaults = useMemo(() => {
    const values: Record<string, unknown> = {};
    paths?.forEach((path) => {
      // String paths only; JSX-element paths are render-only (see useSchema).
      if (!isString(path)) return;
      const [, attribute, , plainPath] = getModelAndAttributeFromPath(
        model,
        path
      );
      const top = toPath(plainPath)[0];
      if (toPath(plainPath).length === 1) {
        // FILTER mode: schemaFromAttribute provided → default to null
        // VALIDATION mode: no builder → derive default from attribute type
        values[top] = schemaFromAttribute
          ? null
          : zodDefaultFromAttribute(attribute!);
      }
    });
    return { ...values, ...extraDefaultValues };
  }, [model, paths, schemaFromAttribute, extraDefaultValues]);

  return defaults;
};

export const useResolver = (schema: z.ZodType) => {
  // The dynamically-built z.object(shape) has unknown output; cast to
  // ZodType<FieldValues, FieldValues> so zodResolver's overloads resolve.
  // Runtime behavior is unchanged — the schema itself validates the values.
  return useMemo(
    () => zodResolver(schema as z.ZodType<FieldValues, FieldValues>),
    [schema]
  );
};

export const useFilterField = (path?: string, operator?: string) => {
  const operatorPath = useMemo(
    () => compact([path, operator]).join('.'),
    [path, operator]
  );

  return { operatorPath };
};

export const useModelFilterField = (
  model: RhinoResource,
  path: string,
  _options: Record<string, unknown> = {}
) => {
  const memoModel = useModel(model);

  const [attributeModel, attribute, operator, plainPath] = useMemo(
    () => getModelAndAttributeFromPath(memoModel, path),
    [memoModel, path]
  );

  const filterField = useFilterField(plainPath, operator);

  // FIXME: Memoize this?
  return { attribute, attributeModel, operator, plainPath, ...filterField };
};
